import React, {
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useState,
  Fragment,
} from "react";
import { Box, TableBody } from "@mui/material";
import {
  Column,
  HeaderGroup,
  Row,
  TableBodyPropGetter,
  TableBodyProps,
  TableToggleAllRowsSelectedProps,
} from "react-table";
import TableHeader from "@/modules/common/components/tables/common-table/body/components/table-header";
import TableRow from "@/modules/common/components/tables/common-table/body/components/table-row";
import useScrollSync from "@/modules/leads/hooks/mobile/scroll-sync.hook";
import useTableBody from "@/modules/common/hooks/common-table.hook/table-body.hook";
import { NoResults } from "@/modules/common/components/ui/no-results";
import { zoomMarginTopMap } from "@/modules/common/constants";
import TableSeparator from "./components/table-separator";

interface TableProps {
  headerGroups: HeaderGroup<object>[];
  prepareRow: (row: Row<object>) => void;
  page?: Row<object>[];
  rows: Row<object>[];
  getTableProps: () => object;
  getTableBodyProps: (
    propGetter?: TableBodyPropGetter<object> | undefined
  ) => TableBodyProps;
  columns: readonly Column<object>[];
  searchValue?: string;
  isLoading?: boolean;
  filters?: any;
  setFilters?: any;
  zoomLevel?: number;
  actionsRef?: React.RefObject<HTMLDivElement>;
  headerMarginTop?: number;
  fixedHeader?: boolean;
  setZoomLevel?: (zoom: number) => void;
  tableName?: string;
  setInitialSortState?: (sortState: {}) => void;
  initialSortState?: {};
  clickableRows?: boolean;
  onFilter?: (value: number) => void;
  getRowBackgroundColor?: (
    row: any,
    selectedColoredRow?: { key: string; value: number | string }
  ) => string | null;
  selectedColoredRow?: { key: string; value: number | string };
  setIsTableChange?: (isTableChange: boolean) => void;
  clearMultiselectValues?: boolean;
  enableRowSelection?: boolean;
  redirectionAccessor?: string;
  clearSortTrigger?: boolean;
  redirectionUrl?: string;
  getToggleAllRowsSelectedProps?: (
    props?: Partial<TableToggleAllRowsSelectedProps> | undefined
  ) => TableToggleAllRowsSelectedProps;
}

const CommonTable: React.FC<TableProps> = ({
  headerGroups,
  prepareRow,
  rows,
  getTableProps,
  getTableBodyProps,
  searchValue,
  isLoading,
  filters,
  setFilters,
  zoomLevel,
  headerMarginTop,
  fixedHeader,
  setZoomLevel,
  tableName,
  setInitialSortState,
  initialSortState,
  clickableRows,
  onFilter,
  getRowBackgroundColor,
  selectedColoredRow,
  setIsTableChange,
  clearMultiselectValues,
  enableRowSelection,
  redirectionAccessor,
  clearSortTrigger,
  redirectionUrl,
  getToggleAllRowsSelectedProps,
}) => {
  const tableRef = useRef<HTMLTableSectionElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  useScrollSync(tableRef, stickyHeaderRef);

  const [extraColumnWidth, setExtraColumnWidth] = useState(0);
  const [minTableWidth, setMinTableWidth] = useState(0);

  const getZoomMarginTop = useCallback(
    (zoom: number) =>
      zoomMarginTopMap[zoom as keyof typeof zoomMarginTopMap] ||
      zoomMarginTopMap[100],
    []
  );

  const headerStyle = useMemo(
    () => ({
      position: "sticky",
      top: !fixedHeader
        ? getZoomMarginTop(zoomLevel || 100) + (headerMarginTop || 0)
        : 0,
      background: "inherit",
      zIndex: 9,
      maxWidth: "100%",
      minHeight: "fit-content",
      overflow: "hidden",
    }),
    [zoomLevel, headerMarginTop, getZoomMarginTop]
  );

  const tableBodyStyle = useMemo(
    () => ({
      display: "flex",
      flexDirection: "column",
      maxWidth: "100%",
      overflowX: "auto",
      overflowY: "hidden",
      zIndex: 2,
    }),
    []
  );

  const { handlePinchStart, handlePinchMove } = useTableBody(
    zoomLevel!,
    setZoomLevel!
  );

  useEffect(() => {
    const totalColumnsWidth = () => {
      return headerGroups[0].headers.reduce((totalWidth, header: any) => {
        return totalWidth + (header.minWidth || 0);
      }, 0);
    };

    const updateTableDimensions = () => {
      if (
        tableRef.current &&
        headerGroups[0]?.headers?.length > 0 &&
        tableRef.current.clientWidth > totalColumnsWidth()
      ) {
        setMinTableWidth(tableRef.current.clientWidth);
        setExtraColumnWidth(
          (tableRef.current.clientWidth - totalColumnsWidth()) /
            headerGroups[0].headers.length
        );
      }
    };

    updateTableDimensions();

    const resizeObserver = new ResizeObserver(updateTableDimensions);
    if (tableRef.current) {
      resizeObserver.observe(tableRef.current);
    }

    return () => {
      if (tableRef.current) {
        resizeObserver.unobserve(tableRef.current);
      }
    };
  }, [rows, headerGroups]);

  const exactMatchRows = useMemo(
    () =>
      rows.filter((row) => {
        const inferredRow = row.original as unknown as { exactMatch: boolean };

        return inferredRow.exactMatch;
      }),
    [rows]
  );

  const similarResults = useMemo(
    () =>
      rows.filter((row) => {
        const inferredRow = row.original as unknown as { exactMatch: boolean };

        return !inferredRow.exactMatch;
      }),
    [rows]
  );

  return (
    <>
      <Box
        sx={headerStyle}
        ref={stickyHeaderRef}
        onTouchStart={handlePinchStart as any}
        onTouchMove={handlePinchMove as any}
      >
        <TableHeader
          headerGroups={headerGroups}
          isLoadingFilter={isLoading}
          filters={filters}
          setFilters={setFilters}
          tableName={tableName}
          setInitialSortState={setInitialSortState}
          initialSortState={initialSortState}
          zoomLevel={zoomLevel}
          setIsTableChange={setIsTableChange}
          clearMultiselectValues={clearMultiselectValues}
          enableRowSelection={enableRowSelection}
          extraColumnWidth={extraColumnWidth}
          minTableWidth={minTableWidth}
          clearSortTrigger={clearSortTrigger}
          getToggleAllRowsSelectedProps={getToggleAllRowsSelectedProps}
        />
      </Box>

      {rows?.length === 0 && !isLoading ? (
        <Box
          sx={{
            width: "100%",
            height: "400px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <NoResults />
        </Box>
      ) : (
        <Box sx={tableBodyStyle} ref={tableRef}>
          <Box
            sx={{
              height: { mobile: "100%" },
            }}
          >
            <Box
              sx={{
                fontFamily: "Regular",
                borderCollapse: "separate",
              }}
              {...getTableProps()}
            >
              <TableBody {...getTableBodyProps()}>
                {exactMatchRows.length > 0 && (
                  <Fragment>
                    <TableSeparator
                      text={`${exactMatchRows.length} Exact Matching Results`}
                      backgroundColor="#B6E9D1"
                      showCheckbox={enableRowSelection}
                    />

                    <TableRow
                      rows={exactMatchRows}
                      prepareRow={prepareRow}
                      headerGroups={headerGroups}
                      searchValue={searchValue}
                      clickableRows={clickableRows}
                      isLoading={isLoading}
                      onFilter={onFilter}
                      getRowBackgroundColor={getRowBackgroundColor}
                      selectedColoredRow={selectedColoredRow}
                      enableRowSelection={enableRowSelection}
                      extraColumnWidth={extraColumnWidth}
                      minTableWidth={minTableWidth}
                      redirectionAccessor={redirectionAccessor}
                      redirectionUrl={redirectionUrl}
                    />

                    <TableSeparator
                      text={`${similarResults.length} Similar Results`}
                      backgroundColor="#FDDDB3"
                      showCheckbox={enableRowSelection}
                    />
                  </Fragment>
                )}

                {similarResults.length > 0 && (
                  <Box
                    sx={{
                      maxWidth: stickyHeaderRef.current?.offsetWidth,
                      minHeight: "50vh",
                    }}
                  >
                    <TableRow
                      rows={similarResults}
                      prepareRow={prepareRow}
                      headerGroups={headerGroups}
                      searchValue={searchValue}
                      clickableRows={clickableRows}
                      isLoading={isLoading}
                      onFilter={onFilter}
                      getRowBackgroundColor={getRowBackgroundColor}
                      selectedColoredRow={selectedColoredRow}
                      enableRowSelection={enableRowSelection}
                      extraColumnWidth={extraColumnWidth}
                      minTableWidth={minTableWidth}
                      redirectionAccessor={redirectionAccessor}
                      redirectionUrl={redirectionUrl}
                    />
                  </Box>
                )}
              </TableBody>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default React.memo(CommonTable);
